import React from 'react';
import type { Pagination } from '@chatsdet/types/issues';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, Button, EmptyState, Flex, LoadingOrbit, Text, Warning } from '@chatsdet/ui';
import { ChatCircleDots, PaperPlaneRight } from '@phosphor-icons/react';
import { CommentEditor, type MentionableMember } from '../../../../features/comments/CommentEditor';
import { CommentBody } from '../../../../features/comments/CommentBody';

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatRelativeTime = (value: string | null) => {
  if (!value) return '—';
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return value;
  }
};

/** Base comment shape accepted by CommentsTab — works for both issue and task comments. */
export type BaseComment = {
  id: string;
  body: string;
  createdAt: string;
  author?: { displayName: string | null; primaryEmail: string | null } | null;
  authorDisplayName?: string | null;
};

/**
 * A non-comment timeline row (e.g. a workflow audit event) interleaved
 * chronologically with comments. Callers own the rendering — `render` gets
 * the same left-gutter/connector-line treatment a comment row gets.
 */
export type TimelineExtraItem = {
  id: string;
  createdAt: string;
  render: React.ReactNode;
};

type CommentsTabProps = {
  comments: BaseComment[];
  pagination: Pagination | undefined;
  page: number;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  commentText: string;
  isPosting: boolean;
  onCommentTextChange: (value: string) => void;
  onAddComment: () => void;
  onPageChange: (page: number) => void;
  className?: string;
  /** When provided, the editor enables `@`-mention typeahead against this list. */
  mentionableMembers?: MentionableMember[];
  /** When provided, node-reference chips in comment bodies become clickable. */
  onNodeClick?: (nodeId: string) => void;
  /**
   * Non-comment rows (e.g. workflow audit events) merged into the timeline by
   * `createdAt`. Optional and backwards-compatible — omitted by every caller
   * except the Flow Designer's Comments tab.
   *
   * `extraItems` reflects the *full, unpaginated* event history, while
   * `comments` may be a single page of a larger, separately-paginated set.
   * Interleaving them on page 2+ of comments would mix recent events into an
   * older comment page, which reads as chronologically incoherent. So this
   * is only interleaved when `pagination` is undefined or its `page` is 1 —
   * on later pages, comments render alone as before.
   */
  extraItems?: TimelineExtraItem[];
};

export function CommentsTab({
  comments,
  pagination,
  page,
  isLoading,
  isError,
  isFetching,
  commentText,
  isPosting,
  onCommentTextChange,
  onAddComment,
  onPageChange,
  className,
  mentionableMembers,
  onNodeClick,
  extraItems,
}: CommentsTabProps) {
  // Reverse to show chronological order (oldest first)
  const chronologicalComments = React.useMemo(() => [...comments].reverse(), [comments]);

  // Merge comments and any extra (e.g. audit event) rows into one
  // chronological timeline, oldest first.
  type TimelineRow =
    | { kind: 'comment'; key: string; createdAt: string; comment: BaseComment }
    | { kind: 'extra'; key: string; createdAt: string; render: React.ReactNode };
  const extrasPage = pagination?.page ?? 1;
  const timelineRows = React.useMemo<TimelineRow[]>(() => {
    const rows: TimelineRow[] = chronologicalComments.map((comment) => ({
      kind: 'comment',
      key: `c-${comment.id}`,
      createdAt: comment.createdAt,
      comment,
    }));
    // extraItems reflect the full event history; only interleave them when
    // comments aren't paginated, or we're on the first (most recent) page —
    // otherwise older comment pages would get recent events mixed in.
    const canInterleaveExtras = extrasPage <= 1;
    if (canInterleaveExtras) {
      for (const item of extraItems ?? []) {
        rows.push({ kind: 'extra', key: `e-${item.id}`, createdAt: item.createdAt, render: item.render });
      }
    }
    rows.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    return rows;
  }, [chronologicalComments, extraItems, extrasPage]);

  return (
    <div className={className ?? 'px-6 py-6 space-y-4'}>
      <section className="space-y-3">
        {isLoading ? (
          <Flex align="center" justify="center" className="py-6">
            <LoadingOrbit size="md" />
          </Flex>
        ) : isError ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            className="border border-dashed border-border rounded-lg py-6 space-y-2"
          >
            <Warning size={32} className="text-destructive" />
            <Text size="sm" className="text-muted-foreground">
              Failed to load comments.
            </Text>
          </Flex>
        ) : timelineRows.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<ChatCircleDots size={22} />}
            title="No comments yet"
            description="Share context, notes, or observations to start the thread."
          />
        ) : (
          <div className="flow-root">
            <ul className="-my-2">
              {timelineRows.map((row, index) => {
                const isLast = index === timelineRows.length - 1;
                if (row.kind === 'extra') {
                  return (
                    <li key={row.key} className="relative flex gap-3 py-3">
                      {!isLast && (
                        <span
                          className="absolute left-4 top-10 -ml-px h-[calc(100%-1.5rem)] w-px bg-border"
                          aria-hidden
                        />
                      )}
                      {row.render}
                    </li>
                  );
                }

                const comment = row.comment;
                const authorName =
                  comment.author?.displayName ??
                  comment.author?.primaryEmail ??
                  comment.authorDisplayName ??
                  'Unknown user';
                const authorEmail = comment.author?.primaryEmail ?? null;
                return (
                  <li key={row.key} className="relative flex gap-3 py-3">
                    {/* Connector line down the avatar gutter (not after the last row) */}
                    {!isLast && (
                      <span
                        className="absolute left-4 top-10 -ml-px h-[calc(100%-1.5rem)] w-px bg-border"
                        aria-hidden
                      />
                    )}
                    <Avatar
                      name={comment.author?.displayName ?? null}
                      email={authorEmail}
                      size="sm"
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <Flex align="baseline" gap="xs" wrap className="min-w-0">
                        <Text size="sm" weight="semibold" className="truncate">
                          {authorName}
                        </Text>
                        {authorEmail &&
                          comment.author?.displayName &&
                          authorEmail !== comment.author.displayName && (
                            <Text size="xs" className="text-muted-foreground truncate">
                              {authorEmail}
                            </Text>
                          )}
                        <Text
                          size="xs"
                          className="text-muted-foreground/70 shrink-0"
                          title={formatDateTime(comment.createdAt)}
                        >
                          · {formatRelativeTime(comment.createdAt)}
                        </Text>
                      </Flex>
                      <div className="text-sm text-foreground/90">
                        <CommentBody body={comment.body} onNodeClick={onNodeClick} />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {pagination && pagination.totalPages > 1 ? (
          <Flex justify="between" align="center">
            <Button
              variant="outline"
              size="sm"
              onPress={() => onPageChange(Math.max(1, page - 1))}
              isDisabled={page <= 1 || isFetching}
            >
              Previous
            </Button>
            <Text size="xs" className="text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages} • {pagination.totalItems} total
            </Text>
            <Button
              variant="outline"
              size="sm"
              onPress={() => onPageChange(page + 1)}
              isDisabled={isFetching || (pagination && page >= pagination.totalPages)}
            >
              Next
            </Button>
          </Flex>
        ) : null}

        {/* Input at bottom */}
        <div className="border-t border-border pt-3 mt-3 space-y-2">
          <CommentEditor
            value={commentText}
            onChange={onCommentTextChange}
            isDisabled={isPosting}
            rows={3}
            mentionableMembers={mentionableMembers}
            onSubmit={onAddComment}
          />
          <Flex justify="between" align="center">
            <Text size="xs" className="text-muted-foreground">
              Shift + Enter to send
            </Text>
            <Button
              variant="primary"
              size="sm"
              iconLeft={<PaperPlaneRight size={16} />}
              onPress={onAddComment}
              isDisabled={isPosting || commentText.trim().length === 0}
            >
              {isPosting ? 'Posting…' : 'Post'}
            </Button>
          </Flex>
        </div>
      </section>
    </div>
  );
}
